import { z } from 'zod'

const envSchema = z.object({
  // Base URL for the app (Vite's BASE_URL)
  BASE_URL: z.string().default('/'),

  // Keycloak - with defaults for development
  VITE_KEYCLOAK_URL: z
    .string()
    .url({ message: 'Invalid Keycloak URL' })
    .default('http://localhost:8080'),
  VITE_KEYCLOAK_REALM: z.string().min(1).default('reqdesk'),
  VITE_KEYCLOAK_CLIENT_ID: z.string().min(1).default('reqdesk-spa'),

  // API - with defaults for development
  VITE_API_BASE_URL: z
    .string()
    .url({ message: 'Invalid API URL' })
    .default('https://localhost:7500'),
  VITE_API_OPENAPI_URL: z.string().url({ message: 'Invalid OpenAPI URL' }).optional(),
  VITE_API_PROXY_URL: z.string().url({ message: 'Invalid proxy URL' }).optional(),

  // App
  VITE_APP_NAME: z.string().default('Reqdesk'),
  VITE_APP_VERSION: z.string().default('1.0.0'),

  // Feature Flags - Fixed: default must be applied before transform
  VITE_ENABLE_PWA: z
    .string()
    .default('false')
    .transform(v => v === 'true'),
  VITE_ENABLE_MONITORING: z
    .string()
    .default('false')
    .transform(v => v === 'true'),
  VITE_ENABLE_DEVTOOLS: z
    .string()
    .default('true')
    .transform(v => v === 'true'),

  // Monitoring (Optional)
  VITE_SENTRY_DSN: z.string().url({ message: 'Invalid Sentry DSN URL' }).optional(),
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_DATADOG_CLIENT_TOKEN: z.string().optional(),

  // Moyasar (Optional in dev — payment-method add flow is disabled without it)
  VITE_MOYASAR_PUBLISHABLE_KEY: z.string().optional(),
})

export type Env = z.infer<typeof envSchema>

// Runtime config injected by docker-entrypoint.sh into window.__ENV
// Falls back to import.meta.env for Vite dev server
const runtimeEnv = (window as unknown as Record<string, unknown>).__ENV as
  | Record<string, string | undefined>
  | undefined

function getEnv(key: string, fallback?: string): string | undefined {
  const runtime = runtimeEnv?.[key]
  if (runtime !== undefined && runtime !== '') return runtime

  const buildTime = (import.meta.env as Record<string, string | undefined>)[key]
  if (buildTime !== undefined && buildTime !== '') return buildTime

  return fallback
}

const envObject = {
  BASE_URL: import.meta.env.BASE_URL || '/',

  VITE_KEYCLOAK_URL: getEnv('VITE_KEYCLOAK_URL', 'http://localhost:8080'),
  VITE_KEYCLOAK_REALM: getEnv('VITE_KEYCLOAK_REALM', 'reqdesk'),
  VITE_KEYCLOAK_CLIENT_ID: getEnv('VITE_KEYCLOAK_CLIENT_ID', 'reqdesk-spa'),

  VITE_API_BASE_URL: getEnv('VITE_API_BASE_URL', 'https://localhost:7500'),
  VITE_API_OPENAPI_URL: getEnv('VITE_API_OPENAPI_URL'),
  VITE_API_PROXY_URL: getEnv('VITE_API_PROXY_URL'),
  VITE_APP_NAME: getEnv('VITE_APP_NAME', 'Reqdesk'),
  VITE_APP_VERSION: getEnv('VITE_APP_VERSION', '1.0.0'),
  VITE_ENABLE_PWA: getEnv('VITE_ENABLE_PWA', 'false'),
  VITE_ENABLE_MONITORING: getEnv('VITE_ENABLE_MONITORING', 'false'),
  VITE_ENABLE_DEVTOOLS: getEnv('VITE_ENABLE_DEVTOOLS', 'true'),
  VITE_SENTRY_DSN: getEnv('VITE_SENTRY_DSN'),
  VITE_POSTHOG_KEY: getEnv('VITE_POSTHOG_KEY'),
  VITE_DATADOG_CLIENT_TOKEN: getEnv('VITE_DATADOG_CLIENT_TOKEN'),
  VITE_MOYASAR_PUBLISHABLE_KEY: getEnv('VITE_MOYASAR_PUBLISHABLE_KEY'),
}

// Parse and validate
const parseResult = envSchema.safeParse(envObject)

// Export the validated environment
export const env: Env = parseResult.success ? parseResult.data : envSchema.parse(envObject) // This will throw with helpful error messages

if (!parseResult.success) {
  console.warn('Environment validation issues:', parseResult.error.issues)
  console.warn('Using default values for missing environment variables')
}

// Log the final configuration (without sensitive data)
console.log('Environment configuration loaded:', {
  keycloakUrl: env.VITE_KEYCLOAK_URL,
  keycloakRealm: env.VITE_KEYCLOAK_REALM,
  keycloakClientId: env.VITE_KEYCLOAK_CLIENT_ID,

  apiBaseUrl: env.VITE_API_BASE_URL,
  appName: env.VITE_APP_NAME,
  appVersion: env.VITE_APP_VERSION,
})
